import { inject, Injectable } from '@angular/core';
import { ConfirmRegistrationRequest } from '@pactasign/utils/lib/auth/requests/confirm-registration-request';
import { RegisterRequest } from '@pactasign/utils/lib/auth/requests/register-request';
import { ResetPasswordRequest } from '@pactasign/utils/lib/auth/requests/reset-password-request';
import { ApiConnector } from 'src/app/core/api/api-connector';

enum AuthApiEndpoint {
    CONFIRM_REGISTRATION = 'confirm-registration',
    REGISTER = 'register',
    RESET_PASSWORD = 'reset-password',
    CONFIRM_PASSWORD_RESET = 'confirm-password-reset',
    VERIFY_RESET_CODE = 'verify-reset-code',
    CHANGE_PASSWORD = 'change-password',
}

@Injectable({
    providedIn: 'root',
})
export class AuthApi {

    private readonly apiConnector: ApiConnector = inject(ApiConnector);

    private static buildPath(endpoint: AuthApiEndpoint): string {
        return `/auth/${ endpoint }`;
    }

    /**
     * Confirms the registration of a user.
     * @param code The confirmation code.
     */
    public async confirmRegistration(code: string): Promise<void> {
        await this.apiConnector.call<ConfirmRegistrationRequest, void>(
            AuthApi.buildPath(AuthApiEndpoint.CONFIRM_REGISTRATION),
            {
                code,
            },
        );
    }

    /**
     * Starts the registration of a user.
     * @param name The name of the user.
     * @param email The email of the user.
     * @param password The password of the user.
     */
    public async register(name: string, email: string, password: string): Promise<void> {
        await this.apiConnector.call<RegisterRequest, void>(
            AuthApi.buildPath(AuthApiEndpoint.REGISTER),
            {
                name,
                email,
                password,
            },
        );
    }

    /**
     * Sends a password reset link to the target email.
     * @param email The target email.
     */
    public async resetPassword(email: string): Promise<void> {
        await this.apiConnector.call<ResetPasswordRequest, void>(
            AuthApi.buildPath(AuthApiEndpoint.RESET_PASSWORD),
            {
                email,
            },
        );
    }

    /** Validates a reset code (throws if invalid/expired) — used by the in-app reset page. */
    public async verifyResetCode(code: string): Promise<void> {
        await this.apiConnector.call<{ code: string }, void>(
            AuthApi.buildPath(AuthApiEndpoint.VERIFY_RESET_CODE),
            { code },
        );
    }

    /** Completes a password reset using the emailed code. */
    public async confirmPasswordReset(code: string, password: string): Promise<void> {
        await this.apiConnector.call<{ code: string, password: string }, void>(
            AuthApi.buildPath(AuthApiEndpoint.CONFIRM_PASSWORD_RESET),
            { code, password },
        );
    }

    /** Changes the password of the logged-in user (reauthenticates with the current password). */
    public async changePassword(currentPassword: string, newPassword: string): Promise<void> {
        await this.apiConnector.call<{ currentPassword: string, newPassword: string }, void>(
            AuthApi.buildPath(AuthApiEndpoint.CHANGE_PASSWORD),
            { currentPassword, newPassword },
        );
    }
}